import React, { useState, useEffect } from 'react';
import api, { API_URL, getServerUrl } from '../../services/api';
import {
  X, Clock, User, CheckCircle, Circle, AlertTriangle,
  Gem, Package, Scale, TrendingUp, TrendingDown, Minus,
  ChevronDown, ChevronRight, Award, Wrench, Star
} from 'lucide-react';

// ─── Utilitaires ────────────────────────────────────────────────────────────
const fmt = (n, dec = 2) => n != null ? Number(n).toFixed(dec) : '—';
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const fmtMin = (min) => {
  if (!min && min !== 0) return '—';
  const h = Math.floor(min / 60), m = min % 60;
  return h > 0 ? `${h}h${m > 0 ? m + 'min' : ''}` : `${m}min`;
};

const EcartBadge = ({ val, unit = '' }) => {
  if (val == null || val === 0) return <span className="text-gray-400">=</span>;
  const pos = val > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded ${pos ? 'bg-red-900/40 text-red-300' : 'bg-green-900/40 text-green-300'}`}>
      {pos ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {pos ? '+' : ''}{val}{unit}
    </span>
  );
};

const StatCard = ({ label, value, sub, color = '#d4af37' }) => (
  <div className="rounded-xl p-4" style={{ background: '#0f0f14', border: '1px solid #1e1e28' }}>
    <div className="text-xs uppercase tracking-wider mb-1" style={{ color: '#555' }}>{label}</div>
    <div className="text-lg font-bold" style={{ color }}>{value}</div>
    {sub && <div className="text-xs mt-0.5" style={{ color: '#555' }}>{sub}</div>}
  </div>
);

// ─── Étape row ───────────────────────────────────────────────────────────────
function EtapeRow({ etape, index }) {
  const [open, setOpen] = useState(index === 0);
  const estPrevu = parseFloat(etape.duree_estimee_heures || 0) > 0;
  const reelMin = Math.round(parseFloat(etape.duree_reelle_heures || 0) * 60);
  const prevMin = Math.round(parseFloat(etape.duree_estimee_heures || 0) * 60);
  const ecart = estPrevu ? reelMin - prevMin : null;
  const aff = etape.affectations?.[0] || null;

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #1e1e28' }}>
      {/* Header cliquable */}
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5"
        style={{ background: '#111118' }}>
        <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
          style={{ background: estPrevu ? '#1e1e28' : '#2a1a0a', color: estPrevu ? '#d4af37' : '#f59e0b', border: `1px solid ${estPrevu ? '#2a2a3a' : '#92400e'}` }}>
          {index + 1}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white truncate">{etape.nom_etape}</span>
            {!estPrevu && (
              <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded font-medium" style={{ background: '#92400e33', color: '#f59e0b', border: '1px solid #92400e66' }}>
                Non prévu
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            {etape.ouvrier_nom?.trim() && (
              <span className="text-xs" style={{ color: '#888' }}>
                <User className="h-3 w-3 inline mr-1" />{etape.ouvrier_nom.trim()}
              </span>
            )}
            {reelMin > 0 && (
              <span className="text-xs" style={{ color: '#888' }}>
                <Clock className="h-3 w-3 inline mr-1" />{fmtMin(reelMin)}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {ecart !== null && <EcartBadge val={ecart} unit="min" />}
          <span className={`text-xs px-2 py-0.5 rounded ${etape.statut === 'terminee' ? 'text-green-400' : 'text-yellow-400'}`}
            style={{ background: etape.statut === 'terminee' ? '#052e1633' : '#42290033' }}>
            {etape.statut === 'terminee' ? '✓ Terminée' : etape.statut}
          </span>
          {open ? <ChevronDown className="h-4 w-4" style={{ color: '#555' }} /> : <ChevronRight className="h-4 w-4" style={{ color: '#555' }} />}
        </div>
      </button>

      {/* Détail */}
      {open && (
        <div className="px-4 py-3 space-y-3" style={{ background: '#0a0a10', borderTop: '1px solid #1e1e28' }}>

          {/* Temps */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-2 rounded-lg" style={{ background: '#111118' }}>
              <div className="text-xs mb-1" style={{ color: '#555' }}>Prévu</div>
              <div className="text-sm font-medium" style={{ color: '#888' }}>{estPrevu ? fmtMin(prevMin) : '—'}</div>
            </div>
            <div className="text-center p-2 rounded-lg" style={{ background: '#111118' }}>
              <div className="text-xs mb-1" style={{ color: '#555' }}>Réel</div>
              <div className="text-sm font-bold" style={{ color: '#d4af37' }}>{fmtMin(reelMin)}</div>
            </div>
            <div className="text-center p-2 rounded-lg" style={{ background: '#111118' }}>
              <div className="text-xs mb-1" style={{ color: '#555' }}>Écart</div>
              <div className="text-sm">{ecart !== null ? <EcartBadge val={ecart} unit="min" /> : <span style={{ color: '#555' }}>—</span>}</div>
            </div>
          </div>

          {/* Pesées / affectation */}
          {aff && (parseFloat(aff.poids_or_donne) > 0 || parseFloat(aff.poids_bijou_fini) > 0) && (
            <div className="rounded-lg p-3 space-y-1.5" style={{ background: '#111118', border: '1px solid #1e1e28' }}>
              <div className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#d4af37' }}>
                <Scale className="h-3 w-3 inline mr-1" />Pesées
              </div>
              {parseFloat(aff.poids_or_donne) > 0 && (
                <div className="flex justify-between text-xs">
                  <span style={{ color: '#888' }}>Or donné à l'ouvrier</span>
                  <span className="font-medium" style={{ color: '#f59e0b' }}>{fmt(aff.poids_or_donne)} g</span>
                </div>
              )}
              {parseFloat(aff.poids_bijou_fini) > 0 && (
                <div className="flex justify-between text-xs">
                  <span style={{ color: '#888' }}>Poids bijou après étape</span>
                  <span className="font-medium text-white">{fmt(aff.poids_bijou_fini)} g</span>
                </div>
              )}
              {parseFloat(aff.or_restant) > 0 && (
                <div className="flex justify-between text-xs">
                  <span style={{ color: '#888' }}>Or restant retourné</span>
                  <span className="font-medium text-green-400">{fmt(aff.or_restant)} g</span>
                </div>
              )}
            </div>
          )}

          {/* Dates */}
          {(etape.date_debut || etape.date_fin) && (
            <div className="flex gap-4 text-xs" style={{ color: '#666' }}>
              {etape.date_debut && <span>Début : {fmtDate(etape.date_debut)}</span>}
              {etape.date_fin && <span>Fin : {fmtDate(etape.date_fin)}</span>}
            </div>
          )}

          {/* Notes */}
          {(etape.notes || aff?.notes) && (
            <div className="text-xs p-2 rounded" style={{ background: '#0f0f14', color: '#888', fontStyle: 'italic' }}>
              "{aff?.notes || etape.notes}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Modal principal ─────────────────────────────────────────────────────────
function HistoriqueProductionModal({ bijouId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('etapes');

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/atelier/fiche-fabrication/bijou/${bijouId}/historique`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [bijouId]);

  if (loading) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-3" style={{ borderColor: '#d4af37', borderTopColor: 'transparent' }} />
        <p className="text-sm" style={{ color: '#888' }}>Chargement historique...</p>
      </div>
    </div>
  );

  if (error || !data) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="p-6 rounded-xl text-center" style={{ background: '#0f0f14', border: '1px solid #2a2a3a' }}>
        <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-red-400" />
        <p className="text-red-400">Erreur : {error || 'Données introuvables'}</p>
        <button onClick={onClose} className="mt-3 px-4 py-2 rounded text-sm" style={{ background: '#1a1a24', color: '#888' }}>Fermer</button>
      </div>
    </div>
  );

  const { bijou, etapes, checklist, mouvements_or, affectations, resume } = data;
  const hasProduction = etapes && etapes.length > 0;

  const tabs = [
    { id: 'etapes', label: 'Étapes', count: etapes?.length },
    { id: 'checklist', label: 'Checklist', count: checklist?.filter(c => c.checked).length + '/' + checklist?.length },
    { id: 'resume', label: 'Résumé' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end" style={{ background: 'rgba(0,0,0,0.8)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>

      {/* Panel latéral (right drawer) */}
      <div className="h-full w-full max-w-2xl flex flex-col shadow-2xl overflow-hidden"
        style={{ background: '#0a0a10' }}>

        {/* ── Header ── */}
        <div className="flex-shrink-0 px-6 py-5" style={{ background: '#0f0f14', borderBottom: '1px solid #1e1e28' }}>
          <div className="flex items-start gap-4">
            {bijou.image_url && (
              <img src={getServerUrl(bijou.image_url)} alt=""
                className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                style={{ border: '1px solid #2a2a3a' }} />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs tracking-widest uppercase mb-1" style={{ color: '#d4af37' }}>
                Historique Production
              </div>
              <h2 className="text-lg font-semibold text-white leading-tight truncate">
                {bijou.bijou_nom}
              </h2>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <span className="text-xs font-mono" style={{ color: '#888' }}>{bijou.reference}</span>
                <span className="text-xs px-2 py-0.5 rounded font-mono" style={{ background: '#1a1a28', color: '#d4af37', border: '1px solid #2a2a3a' }}>
                  {bijou.id_produit}
                </span>
              </div>
              {bijou.client_nom && (
                <div className="text-xs mt-1" style={{ color: '#666' }}>
                  <User className="h-3 w-3 inline mr-1" />Client : {bijou.client_nom}
                </div>
              )}
            </div>
            <button onClick={onClose} className="p-2 rounded-lg transition-colors hover:bg-white/5 flex-shrink-0" style={{ color: '#555' }}>
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* OF info */}
          {bijou.numero_ordre && (
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              <span style={{ color: '#888' }}>OF : <span className="text-white font-medium">{bijou.numero_ordre}</span></span>
              {bijou.lanceur_nom && (
                <span style={{ color: '#888' }}>Lancé par : <span className="text-white">{bijou.lanceur_nom}</span>
                  <span className="ml-1" style={{ color: '#555' }}>({bijou.lanceur_login})</span>
                </span>
              )}
              {bijou.of_statut && (
                <span className={`px-2 py-0.5 rounded ${bijou.of_statut === 'termine' ? 'text-green-400' : 'text-yellow-400'}`}
                  style={{ background: bijou.of_statut === 'termine' ? '#052e1633' : '#42290033' }}>
                  {bijou.of_statut === 'termine' ? '✓ Terminé' : bijou.of_statut}
                </span>
              )}
            </div>
          )}
        </div>

        {/* ── Stats rapides ── */}
        {hasProduction && (
          <div className="flex-shrink-0 grid grid-cols-4 gap-2 px-6 py-4" style={{ borderBottom: '1px solid #1e1e28' }}>
            <StatCard label="Étapes" value={resume.nb_etapes_total} sub={resume.nb_etapes_non_prevues > 0 ? `${resume.nb_etapes_non_prevues} surprise` : 'toutes prévues'} />
            <StatCard label="Temps réel" value={fmtMin(resume.temps_reel_minutes)}
              sub={resume.temps_estime_minutes > 0 ? `prévu ${fmtMin(resume.temps_estime_minutes)}` : ''} />
            <StatCard label="Or donné" value={`${fmt(resume.or_total_donne)} g`}
              sub={resume.poids_or_reel > 0 ? `reel ${fmt(resume.poids_or_reel)} g` : ''} />
            <StatCard label="Prix final" value={`${fmt(bijou.prix_vente)} DH`}
              sub={bijou.devis_prix > 0 ? `devis ${fmt(bijou.devis_prix)} DH` : ''} color="#10b981" />
          </div>
        )}

        {/* ── Tabs ── */}
        <div className="flex-shrink-0 flex gap-0 px-6 pt-4" style={{ borderBottom: '1px solid #1e1e28' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className="px-4 py-2 text-xs font-medium transition-colors relative"
              style={{ color: activeTab === t.id ? '#d4af37' : '#555' }}>
              {t.label}
              {t.count != null && <span className="ml-1.5 opacity-60">({t.count})</span>}
              {activeTab === t.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t" style={{ background: '#d4af37' }} />
              )}
            </button>
          ))}
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">

          {/* TAB : Étapes */}
          {activeTab === 'etapes' && (
            <>
              {!hasProduction ? (
                <div className="text-center py-12" style={{ color: '#555' }}>
                  <Wrench className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p>Aucune étape de production enregistrée</p>
                </div>
              ) : (
                etapes.map((ep, i) => <EtapeRow key={ep.id || i} etape={ep} index={i} />)
              )}
            </>
          )}

          {/* TAB : Checklist */}
          {activeTab === 'checklist' && (
            <div className="space-y-2">
              {checklist.length === 0 ? (
                <p className="text-center py-8" style={{ color: '#555' }}>Aucune checklist</p>
              ) : (
                checklist.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl"
                    style={{ background: '#0f0f14', border: `1px solid ${item.checked ? '#052e1666' : '#1e1e28'}` }}>
                    <div className="flex-shrink-0 mt-0.5">
                      {item.checked
                        ? <CheckCircle className="h-4 w-4 text-green-400" />
                        : <Circle className="h-4 w-4" style={{ color: '#444' }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm" style={{ color: item.checked ? '#e5e7eb' : '#555' }}>
                        {item.libelle}
                      </div>
                      {item.checked && (
                        <div className="mt-1 flex flex-wrap gap-3 text-xs" style={{ color: '#666' }}>
                          {item.validateur_nom && (
                            <span>
                              <Award className="h-3 w-3 inline mr-0.5 text-yellow-500" />
                              {item.validateur_nom}
                              {item.validateur_login && <span className="ml-1 opacity-60">@{item.validateur_login}</span>}
                            </span>
                          )}
                          {item.date_check && <span>{fmtDate(item.date_check)}</span>}
                        </div>
                      )}
                    </div>
                    <span className="text-xs flex-shrink-0" style={{ color: '#333' }}>#{item.numero_etape}</span>
                  </div>
                ))
              )}
            </div>
          )}

          {/* TAB : Résumé */}
          {activeTab === 'resume' && (
            <div className="space-y-4">

              {/* Devis vs Réel */}
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #1e1e28' }}>
                <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider" style={{ background: '#0f0f14', color: '#d4af37', borderBottom: '1px solid #1e1e28' }}>
                  Comparaison Devis vs Réel
                </div>
                <div className="divide-y" style={{ divideColor: '#1e1e28' }}>
                  {[
                    {
                      label: 'Temps fabrication',
                      prevu: fmtMin(resume.temps_estime_minutes),
                      reel: fmtMin(resume.temps_reel_minutes),
                      ecart: resume.ecart_temps_minutes,
                      unit: 'min'
                    },
                    {
                      label: 'Or utilisé (g)',
                      prevu: fmt(resume.poids_or_estime),
                      reel: fmt(resume.or_total_donne),
                      ecart: resume.or_total_donne - resume.poids_or_estime > 0.001 ? parseFloat((resume.or_total_donne - resume.poids_or_estime).toFixed(2)) : 0,
                      unit: 'g'
                    },
                    {
                      label: 'Prix final',
                      prevu: resume.prix_devis > 0 ? `${fmt(resume.prix_devis)} DH` : '—',
                      reel: `${fmt(resume.prix_reel)} DH`,
                      ecart: resume.prix_devis > 0 ? parseFloat((resume.prix_reel - resume.prix_devis).toFixed(2)) : null,
                      unit: ' DH'
                    },
                  ].map((row, i) => (
                    <div key={i} className="grid grid-cols-4 gap-2 px-4 py-3 text-xs" style={{ background: '#0a0a10' }}>
                      <span style={{ color: '#888' }}>{row.label}</span>
                      <span className="text-center" style={{ color: '#666' }}>{row.prevu}</span>
                      <span className="text-center font-medium text-white">{row.reel}</span>
                      <span className="text-right">{row.ecart != null ? <EcartBadge val={row.ecart} unit={row.unit} /> : <span style={{ color: '#444' }}>—</span>}</span>
                    </div>
                  ))}
                  {/* Header */}
                  <div className="grid grid-cols-4 gap-2 px-4 py-2 text-xs" style={{ background: '#0f0f14', color: '#444', order: -1 }}>
                    <span>Indicateur</span>
                    <span className="text-center">Devis</span>
                    <span className="text-center">Réel</span>
                    <span className="text-right">Écart</span>
                  </div>
                </div>
              </div>

              {/* Ouvriers impliqués */}
              {affectations && affectations.length > 0 && (
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #1e1e28' }}>
                  <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider" style={{ background: '#0f0f14', color: '#d4af37', borderBottom: '1px solid #1e1e28' }}>
                    Ouvriers intervenus
                  </div>
                  {(() => {
                    // Regrouper par ouvrier
                    const byOuvrier = {};
                    affectations.forEach(a => {
                      const nom = (a.ouvrier_nom || '').trim() || `Ouvrier #${a.ouvrier_id}`;
                      if (!byOuvrier[nom]) byOuvrier[nom] = { missions: [], temps: 0 };
                      byOuvrier[nom].missions.push(a.mission);
                    });
                    etapes.forEach(ep => {
                      const nom = (ep.ouvrier_nom || '').trim();
                      if (nom && byOuvrier[nom]) {
                        byOuvrier[nom].temps += Math.round(parseFloat(ep.duree_reelle_heures || 0) * 60);
                      }
                    });
                    return Object.entries(byOuvrier).map(([nom, info], i) => (
                      <div key={i} className="flex items-start gap-3 px-4 py-3" style={{ borderTop: i > 0 ? '1px solid #1e1e28' : 'none', background: '#0a0a10' }}>
                        <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                          style={{ background: '#1e1e28', color: '#d4af37' }}>{nom[0]}</div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-white">{nom}</div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {[...new Set(info.missions)].map((m, j) => (
                              <span key={j} className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#1e1e28', color: '#888' }}>{m}</span>
                            ))}
                          </div>
                        </div>
                        {info.temps > 0 && (
                          <span className="text-xs flex-shrink-0" style={{ color: '#d4af37' }}>{fmtMin(info.temps)}</span>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              )}

              {/* Qui a lancé + infos OF */}
              <div className="rounded-xl p-4 space-y-2" style={{ background: '#0f0f14', border: '1px solid #1e1e28' }}>
                <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#d4af37' }}>
                  Traçabilité OF
                </div>
                {[
                  ['Ordre de fabrication', bijou.numero_ordre],
                  ['Devis', bijou.numero_devis],
                  ['Client', bijou.client_nom],
                  ['Lancé par', bijou.lanceur_nom ? `${bijou.lanceur_nom} (@${bijou.lanceur_login})` : '—'],
                  ['Date lancement', fmtDate(bijou.of_created_at)],
                  ['Début production', fmtDate(bijou.date_debut_production)],
                  ['Fin production', fmtDate(bijou.date_fin_production)],
                  ['Checklist', `${resume.checklist_valides}/${resume.checklist_total} validés`],
                ].map(([k, v], i) => v ? (
                  <div key={i} className="flex justify-between text-xs">
                    <span style={{ color: '#666' }}>{k}</span>
                    <span style={{ color: '#ccc' }}>{v}</span>
                  </div>
                ) : null)}
              </div>

            </div>
          )}

        </div>

        {/* ── Footer ── */}
        <div className="flex-shrink-0 px-6 py-3" style={{ borderTop: '1px solid #1e1e28', background: '#0f0f14' }}>
          <button onClick={onClose}
            className="w-full py-2.5 rounded-xl text-sm font-medium transition-colors"
            style={{ background: '#1a1a24', color: '#888', border: '1px solid #2a2a3a' }}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

export default HistoriqueProductionModal;
